import { useEffect, useRef, useState } from 'react';

type CommitRangeInputProps = {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  onCommit: (value: number) => void;
  formatValue?: (value: number) => string;
  disabled?: boolean;
  anchorValue?: number;
};

export function CommitRangeInput({ label, min, max, step, value, onCommit, formatValue, disabled = false, anchorValue }: CommitRangeInputProps): React.JSX.Element {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastCommitted = useRef(value);

  useEffect(() => {
    setDraft(value);
    lastCommitted.current = value;
  }, [value]);

  const commit = (): void => {
    const next = Number(inputRef.current?.value);
    if (!Number.isFinite(next) || next === lastCommitted.current) return;
    lastCommitted.current = next;
    onCommit(next);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    let next = Number(event.target.value);
    if (anchorValue !== undefined && anchorValue >= min && anchorValue <= max) {
      const trackWidth = event.currentTarget.getBoundingClientRect().width;
      const snapDistance = (max - min) * Math.min(0.035, 8 / Math.max(trackWidth, 1));
      if (Math.abs(next - anchorValue) <= snapDistance) next = anchorValue;
    }
    setDraft(next);
  };

  const anchorPosition = anchorValue === undefined ? undefined : ((anchorValue - min) / (max - min)) * 100;

  return (
    <label>{label}{formatValue ? ` · ${formatValue(draft)}` : ''}
      <span className="anchored-range">
        {anchorPosition !== undefined && <i className="range-anchor" style={{ left: `${anchorPosition}%` }} aria-hidden="true" />}
        <input
          ref={inputRef}
          type="range"
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          value={draft}
          onChange={handleChange}
          onPointerUp={commit}
          onKeyUp={commit}
          onBlur={commit}
        />
      </span>
    </label>
  );
}
